
import {
  Box,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react'
import { Search, Type, Image, Video, Quote, List, Code, Layout, Minus } from 'lucide-react'
import { useState } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { BlockDefinition, BlockCategory, BlockType } from '@/types/pageBuilder'
import { DraggableBlockItem } from './DraggableBlockItem'

interface BlockLibraryProps {
  onSelectBlock: (type: BlockType) => void
  searchQuery?: string
  onSearchChange?: (query: string) => void
}

const blockDefinitions: BlockDefinition[] = [
  { type: 'heading', label: 'Heading', category: 'text', icon: 'Type', description: 'Add a heading', defaultContent: { text: 'Heading', level: 2, alignment: 'left' } },
  { type: 'text', label: 'Paragraph', category: 'text', icon: 'Type', description: 'Add a text paragraph', defaultContent: { text: 'Enter your text here...', alignment: 'left' } },
  { type: 'image', label: 'Image', category: 'media', icon: 'Image', description: 'Add an image', defaultContent: { url: '', alt: '', caption: '', alignment: 'center', width: '100%' } },
  { type: 'video', label: 'Video', category: 'media', icon: 'Video', description: 'Add a video', defaultContent: { url: '', caption: '', alignment: 'center', autoplay: false, controls: true } },
  { type: 'button', label: 'Button', category: 'design', icon: 'Button', description: 'Add a button', defaultContent: { text: 'Button', url: '#', style: 'primary', alignment: 'left' } },
  { type: 'columns', label: 'Columns', category: 'layout', icon: 'Layout', description: 'Add a multi-column layout', defaultContent: { columns: 2, gap: 'medium', blocks: [] } },
  { type: 'quote', label: 'Quote', category: 'text', icon: 'Quote', description: 'Add a quote block', defaultContent: { text: 'Quote text', author: '', alignment: 'left' } },
  { type: 'list', label: 'List', category: 'text', icon: 'List', description: 'Add a list', defaultContent: { items: ['Item 1', 'Item 2'], type: 'ul', alignment: 'left' } },
  { type: 'spacer', label: 'Spacer', category: 'design', icon: 'Minus', description: 'Add vertical space', defaultContent: { height: 40 } },
  { type: 'divider', label: 'Divider', category: 'design', icon: 'Minus', description: 'Add a horizontal divider', defaultContent: { style: 'solid' } },
  { type: 'code', label: 'Code', category: 'text', icon: 'Code', description: 'Add code block', defaultContent: { code: '', language: 'javascript' } },
  { type: 'html', label: 'HTML', category: 'text', icon: 'Code', description: 'Add custom HTML', defaultContent: { html: '' } },
]

const categories: BlockCategory[] = [
  { id: 'all', label: 'All Blocks', blocks: blockDefinitions.map(b => b.type) },
  { id: 'text', label: 'Text', blocks: ['heading', 'text', 'quote', 'list', 'code', 'html'] },
  { id: 'media', label: 'Media', blocks: ['image', 'video'] },
  { id: 'layout', label: 'Layout', blocks: ['columns'] },
  { id: 'design', label: 'Design', blocks: ['button', 'spacer', 'divider'] },
]

export function BlockLibrary({ onSelectBlock, searchQuery = '', onSearchChange }: BlockLibraryProps) {
  const colors = useThemeColors()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [search, setSearch] = useState(searchQuery)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    onSearchChange?.(value)
  }

  const filteredBlocks = blockDefinitions.filter(block => {
    const matchesCategory = selectedCategory === 'all' || block.category === selectedCategory
    const matchesSearch = search === '' || 
      block.label.toLowerCase().includes(search.toLowerCase()) ||
      block.description?.toLowerCase().includes(search.toLowerCase())
    return matchesCategory && matchesSearch
  })


  return (
    <Box
      w="100%"
      h="100%"
      bg={colors.cardBg}
      display="flex"
      flexDirection="column"
    >
      {/* Search */}
      <Box p={3} borderBottom="1px" borderColor={colors.border}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <Search size={16} color={colors.textMuted} />
          </InputLeftElement>
          <Input
            placeholder="Search blocks..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            bg={colors.sectionBg}
            borderColor={colors.border}
          />
        </InputGroup>
      </Box>

      {/* Categories */}
      <Box
        p={2}
        borderBottom="1px"
        borderColor={colors.border}
        overflowX="auto"
        css={{
          '&::-webkit-scrollbar': {
            height: '4px',
          },
        }}
      >
        <HStack spacing={2}>
          {categories.map((category) => (
            <Box
              key={category.id}
              px={3}
              py={1.5}
              borderRadius="md"
              cursor="pointer"
              bg={selectedCategory === category.id ? 'blue.500' : 'transparent'}
              color={selectedCategory === category.id ? 'white' : colors.text}
              _hover={{
                bg: selectedCategory === category.id ? 'blue.600' : colors.sectionBg,
              }}
              onClick={() => setSelectedCategory(category.id)}
              fontSize="xs"
              fontWeight="medium"
              whiteSpace="nowrap"
            >
              {category.label}
            </Box>
          ))}
        </HStack>
      </Box>

      {/* Blocks List */}
      <Box flex="1" overflowY="auto" p={2}>
        <VStack spacing={1} align="stretch">
          {filteredBlocks.map((block) => (
            <DraggableBlockItem
              key={block.type}
              block={block}
              onClick={() => onSelectBlock(block.type)}
            />
          ))}
        </VStack>
      </Box>
    </Box>
  )
}

